import * as React from "react";

export interface VehiclePinProps extends React.SVGProps<SVGSVGElement> {
  on?: boolean;
  pulse?: boolean;
  alertPulse?: boolean;
  rotation?: number;
  idle?: boolean;
  outline?: string;
  hideShadow?: boolean;
  pulseFill?: string;
}

function SvgVehiclePin({
  fill = "#0DA6FD",
  outline = "white",
  rotation,
  pulse,
  alertPulse,
  on = true,
  idle,
  hideShadow,
  pulseFill = fill,
  ...svgProps
}: VehiclePinProps): React.ReactElement {
  const Pulse = (): React.ReactElement => (
    <>
      <circle cx="21" cy="21" r="21" fill={pulseFill} style={{ zIndex: 1 }}>
        <animate attributeName="r" begin="0s" dur="1.5s" repeatCount="indefinite" from="25%" to="50%" />
        <animate attributeName="opacity" begin="0s" dur="1.5s" repeatCount="indefinite" from={1} to={0.5} />
      </circle>
      <circle cx="21" cy="21" r="21" fill={pulseFill} style={{ zIndex: 1 }}>
        <animate attributeName="opacity" begin="0s" dur="1.5s" repeatCount="indefinite" from={0.5} to={0} />
      </circle>
    </>
  );

  const AlertPulse = (): React.ReactElement => (
    <>
      <circle cx="21" cy="21" r="21" fill={pulseFill} style={{ zIndex: 1 }}>
        <animate
          attributeName="r"
          begin="0s"
          dur="0.8s"
          repeatCount="indefinite"
          values="20%;45%;45%"
          keyTimes="0;0.7;1"
        />
        <animate
          attributeName="opacity"
          begin="0s"
          dur="0.8s"
          repeatCount="indefinite"
          values="0.5;0;0"
          keyTimes="0;0.9;1"
        />
      </circle>
      <circle cx="21" cy="21" r="21" stroke={pulseFill} strokeWidth="0.5" style={{ zIndex: 1 }}>
        <animate
          attributeName="r"
          begin="0s"
          dur="0.8s"
          repeatCount="indefinite"
          values="20%;45%;45%"
          keyTimes="0;0.7;1"
        />
        <animate
          attributeName="opacity"
          begin="0s"
          dur="0.8s"
          repeatCount="indefinite"
          values="1;1;0.5"
          keyTimes="0;0.6;1"
        />
      </circle>
      <circle cx="21" cy="21" r="19" stroke={pulseFill} strokeWidth="0.5" style={{ zIndex: 1 }}>
        <animate
          attributeName="opacity"
          begin="0s"
          dur="0.8s"
          repeatCount="indefinite"
          values="0.5;0;0"
          keyTimes="0;0.6;1"
        />
      </circle>
    </>
  );

  if (!on) {
    if (pulse || alertPulse) {
      return (
        <svg
          data-testid="vehicle-pin__off--pulse"
          width="42"
          height="42"
          viewBox="0 0 42 42"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          {...svgProps}
        >
          {alertPulse ? <AlertPulse data-testid="alert-pulse" /> : <Pulse data-testid="pulse" />}
          <g filter="url(#filter0_d)" style={{ opacity: idle ? 0.5 : 1 }}>
            <rect
              x="13"
              y="13"
              width="16"
              height="16"
              rx="4.8"
              fill={fill}
              stroke={outline}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </g>
          <defs>
            <filter
              id="filter0_d"
              x="9"
              y="11"
              width="24"
              height="24"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="2" />
              <feGaussianBlur stdDeviation="1" />
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
            </filter>
          </defs>
        </svg>
      );
    }

    return (
      <svg
        data-testid="vehicle-pin__off"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...svgProps}
      >
        <g filter={!hideShadow ? "url(#filter0_d)" : ""} style={{ opacity: idle ? 0.5 : 1 }}>
          <rect
            x="4"
            y="1.6"
            width="16"
            height="16"
            rx="4.8"
            fill={fill}
            stroke={outline}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        </g>
        <defs>
          <filter
            id="filter0_d"
            x="0"
            y="0"
            width="24"
            height="24"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="1" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
          </filter>
        </defs>
      </svg>
    );
  }

  if (rotation === undefined) {
    if (pulse || alertPulse) {
      return (
        <svg
          data-testid="vehicle-pin--pulse"
          width="42"
          height="42"
          viewBox="0 0 42 42"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          {...svgProps}
        >
          {alertPulse ? <AlertPulse data-testid="alert-pulse" /> : <Pulse data-testid="pulse" />}
          <g filter={!hideShadow ? "url(#filter0_d)" : ""} style={{ opacity: idle ? 0.5 : 1 }}>
            <rect
              x="12"
              y="12"
              width="18"
              height="18"
              rx="9"
              fill={fill}
              stroke={outline}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </g>
          <defs>
            <filter
              id="filter0_d"
              x="9"
              y="11"
              width="24"
              height="24"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="2" />
              <feGaussianBlur stdDeviation="1" />
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
            </filter>
          </defs>
        </svg>
      );
    }

    return (
      <svg
        data-testid="vehicle-pin"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...svgProps}
      >
        <g filter={!hideShadow ? "url(#filter0_d)" : ""} style={{ opacity: idle ? 0.5 : 1 }}>
          <rect
            x="3"
            y="0.999998"
            width="18"
            height="18"
            rx="9"
            fill={fill}
            stroke={outline}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        </g>
        <defs>
          <filter
            id="filter0_d"
            x="0"
            y="-1.90735e-06"
            width="24"
            height="24"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="1" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
          </filter>
        </defs>
      </svg>
    );
  }

  if (pulse || alertPulse) {
    return (
      <svg
        data-testid="vehicle-pin__rotated--pulse"
        width="42"
        height="42"
        viewBox="0 0 42 42"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...svgProps}
        style={{ transform: rotation !== undefined ? `rotate(${rotation}deg)` : undefined }}
      >
        {alertPulse ? <AlertPulse data-testid="alert-pulse" /> : <Pulse data-testid="pulse" />}
        <g filter="url(#filter0_d)" style={{ opacity: idle ? 0.5 : 1 }}>
          <path
            d="M24.8414 12.3911L31.5001 25.4758C33.0104 28.4438 30.9165 32 27.6586 32C26.6955 32 25.8109 31.5907 25.3288 31.3571C24.97 31.1832 24.533 30.9469 24.1395 30.7341L24.1386 30.7337C23.9525 30.6331 23.7762 30.5377 23.6225 30.4567C22.4362 29.8317 21.5731 29.503 20.8955 29.5093C20.2639 29.5151 19.46 29.8355 18.3183 30.4581C18.1836 30.5315 18.0324 30.6159 17.8726 30.7049C17.4802 30.9237 17.0362 31.1712 16.6607 31.3563C16.1785 31.5942 15.3066 32 14.3414 32C11.0835 32 8.98957 28.4438 10.4999 25.4758L17.1586 12.3911C18.7809 9.20297 23.2191 9.20297 24.8414 12.3911Z"
            fill={outline}
          />
          <path
            d="M19.352 13.2258C20.048 11.8581 21.952 11.8581 22.648 13.2258L29.5855 26.8585C30.2335 28.1318 29.3352 29.6575 27.9375 29.6575C26.5398 29.6575 23.7094 27.0363 20.8673 27.0625C18.1257 27.0878 15.4602 29.6575 14.0625 29.6575C12.6648 29.6575 11.7665 28.1318 12.4145 26.8585L19.352 13.2258Z"
            fill={fill}
          />
        </g>
        <defs>
          <filter
            id="filter0_d"
            x="8"
            y="10"
            width="26"
            height="26"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="1" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
          </filter>
        </defs>
      </svg>
    );
  }

  return (
    <svg
      data-testid="vehicle-pin__rotated"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...svgProps}
      style={{ transform: rotation !== undefined ? `rotate(${rotation}deg)` : undefined }}
    >
      <g filter={!hideShadow ? "url(#filter0_d)" : ""} style={{ opacity: idle ? 0.5 : 1 }}>
        <path
          d="M15.4922 2.17371L21.5455 14.0689C22.9186 16.767 21.015 20 18.0533 20C17.1777 20 16.3736 19.6279 15.9353 19.4156C15.6091 19.2575 15.2119 19.0427 14.8541 18.8492L14.8541 18.8492L14.8538 18.849C14.6844 18.7575 14.524 18.6707 14.3841 18.597C13.3056 18.0288 12.521 17.73 11.905 17.7357C11.3308 17.741 10.6 18.0323 9.56207 18.5983C9.43968 18.665 9.30217 18.7417 9.1569 18.8227C8.80014 19.0215 8.39658 19.2465 8.05521 19.4149C7.61684 19.6311 6.82415 20 5.9467 20C2.98498 20 1.08143 16.767 2.45448 14.0689L8.50777 2.17371C9.98267 -0.724571 14.0173 -0.72457 15.4922 2.17371Z"
          fill={outline}
        />
        <path
          d="M10.5018 2.93254C11.1345 1.68915 12.8655 1.68915 13.4982 2.93255L19.805 15.3259C20.3941 16.4835 19.5774 17.8704 18.3068 17.8704C17.0362 17.8704 14.4631 15.4876 11.8793 15.5114C9.38702 15.5344 6.96378 17.8704 5.69317 17.8704C4.42257 17.8704 3.60592 16.4835 4.19498 15.3259L10.5018 2.93254Z"
          fill={fill}
        />
      </g>
      <defs>
        <filter
          id="filter0_d"
          x="0"
          y="0"
          width="24"
          height="24"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}

export default SvgVehiclePin;
